import React from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Typography, Chip } from "@mui/material";
import ColorInfo from "./ColorInfo";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useQuestionNavigator } from "../../../hooks/default/useQuestionNavigator";
import { useQuestionRecord } from "../../../context/QuestionRecord";
import { useProfile } from "../../../context/ProfileContext";
import user from "../../../images/user.png";

function SideBar({
  subjects,
  setCurrentQuestion,
  setCurrentSubject,
  setSubjectId,
  currentQuestion,
  questionData,
  setCurrentQuestionBySubject,
  toggleSubject,
  expandedSubject,
  setExpandedSubject,
  handleOpenInstructions,
}) {
  const { profile } = useProfile();
  // ✅ Function to calculate global question index
  const getGlobalIndex = (subjectIndex, questionIndex, subjectId) => {
    let offset = 0;

    for (let j = 0; j < subjectIndex; j++) {
      offset += subjects[j].subject_question_count;
    }

    setCurrentSubject(expandedSubject);
    setSubjectId(subjectId);
    return offset + questionIndex;
  };
  // "notVisited"
  // not answered
  // "attempted"
  // "marked"

  const {
    questions,
    answered,
    marked,
    notVisited,
    notAnswered,
    markedWithAnswered,
    visitedQuestions,
  } = useQuestionNavigator({
    currentQuestion,
    questionData,
  });

  const { getQuestionRecordById } = useQuestionRecord();
  // attempt_status

  const statusColor = (id) => {
    const attempt_status = getQuestionRecordById(id)?.attempt_status;

    switch (attempt_status) {
      case "attempted":
        return "blue";
      case "marked":
        return "purple";
      case "not answered":
        return "#d1d1d1ff";
      default:
        return "white";
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        width: "100%",
      }}
    >
      <Box>
        {/* ── User Info Section (Mobile/Tablet only) ── */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            gap: "12px",
            padding: "16px 12px",
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fcfcfc",
          }}
        >
          <img src={user} alt="user" height={32} />
          <Typography
            fontWeight="bold"
            sx={{ fontSize: "0.95rem", color: "#333", whiteSpace: "nowrap" }}
          >
            {profile?.full_name}
          </Typography>
        </Box>

        {/* ── Instruction Button (Mobile/Tablet only) ── */}
        <Box
          onClick={handleOpenInstructions}
          sx={{
            display: { xs: "flex", md: "none" },
            alignItems: "center",
            gap: "12px",
            padding: "12px 12px",
            cursor: "pointer",
            borderBottom: "1px solid #e0e0e0",
            "&:hover": { backgroundColor: "#f5f5f5" },
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 24, color: "#555" }} />
          <Typography
            sx={{ fontSize: "0.95rem", color: "#333", fontWeight: 500 }}
          >
            Instructions
          </Typography>
        </Box>

        <Box
          sx={{
            maxHeight: {
              xs: "calc(100vh - 250px)",
              sm: "calc(100vh - 240px)",
              md: "calc(100vh - 180px)",
            },
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": {
              background: "#ccc",
              borderRadius: "4px",
            },
          }}
        >
          {subjects?.map((subject, subjectIndex) => (
            <Box key={subject.id} sx={{ borderBottom: "1px solid #e0e0e0" }}>
              {/* Subject header */}
              <Box
                onClick={() => toggleSubject(subject.subject_name)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 12px",
                  cursor: "pointer",
                  backgroundColor:
                    expandedSubject === subject.subject_name
                      ? "#e3f2fd"
                      : "#f9f9f9",
                  transition: "background 0.2s ease",
                  "&:hover": { background: "#e8f4f8" },
                }}
              >
                <Typography
                  sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#333" }}
                >
                  {subject.subject_name}
                </Typography>
                {expandedSubject === subject.subject_name ? (
                  <ExpandMoreIcon sx={{ fontSize: 18, color: "#1976d2" }} />
                ) : (
                  <ChevronRightIcon sx={{ fontSize: 18, color: "#888" }} />
                )}
              </Box>

              {/* Question number grid */}
              {expandedSubject === subject.subject_name && (
                <Box
                  sx={{
                    padding: "10px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {subject.questions.map((que, i) => {
                    let offset = 0;
                    for (let j = 0; j < subjectIndex; j++) {
                      offset += subjects[j].subject_question_count;
                    }
                    const isActive = currentQuestion === offset + i;
                    const bg = statusColor(que.id);
                    const isColored = bg !== "#fff" && bg !== "#d1d1d1";

                    return (
                      <Box
                        key={i}
                        onClick={() => {
                          setCurrentQuestion(
                            getGlobalIndex(subjectIndex, i, que.subject_id)
                          );
                          setCurrentQuestionBySubject(i);
                        }}
                        style={{
                          border: isActive
                            ? "2px solid black"
                            : "1px solid black",
                          fontWeight: isActive ? 500 : 400,
                          padding: "4px 10px",
                          background: statusColor(que.id),
                          color:
                            statusColor(que.id) === "blue" ||
                              statusColor(que.id) === "purple"
                              ? "white"
                              : "black",
                          borderRadius: "5px",
                          cursor: "pointer",
                          fontSize: "1rem",
                          boxShadow: isActive
                            ? "0 0 0 2px rgba(25,118,210,0.25)"
                            : "none",
                          transition: "all 0.15s",
                          "&:hover": {
                            borderColor: "#1976d2",
                            transform: "scale(1.08)",
                          },
                        }}
                      >
                        {i + 1}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      <ColorInfo
        answered={answered}
        marked={Number(marked) + Number(markedWithAnswered)}
        notAnswered={notAnswered}
        visitedQuestions={visitedQuestions}
        notVisited={notVisited}
      />
    </Box>
  );
}

export default SideBar;
